import React, { useEffect, useState } from "react";
import { fmtDateTimeFirm } from "@/lib/dt";
import { toast } from "sonner";
import api, { toErrMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ShieldCheck, KeyRound, Copy, Download, RefreshCw } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { translations as _tr, t as _tt } from "@/lib/i18n";   // STEP 7b · D2: the card in the screen language (translation only)

/**
 * Two-Factor Authentication settings card + regenerate-backup-codes flow.
 * Rendered in Settings for every signed-in user.
 */
export default function TwoFactorSection() {
  const { t: _t } = useI18n() || {};
  const tr = (k) => (_t ? _t(`s7b.twofa.${k}`) : _tt(_tr.en, `s7b.twofa.${k}`));   // no provider (tests): English
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showCodes, setShowCodes] = useState(false);
  const [codes, setCodes] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/auth/2fa/status");
      setStatus(r.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const regenerate = async () => {
    setConfirmOpen(false);
    setBusy(true);
    try {
      const r = await api.post("/auth/2fa/backup-codes/regenerate");
      setCodes(r.data.codes || []);
      setShowCodes(true);
      toast.success(tr("issued"));
      await load();
    } catch (e) {
      toast.error(toErrMsg(e, tr("regenFailed")));
    } finally { setBusy(false); }
  };

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      toast.success(tr("copied"));
    } catch { toast.error(tr("copyFailed")); }
  };
  const download = () => {
    const blob = new Blob(
      [`AZEX Backup Codes\nGenerated: ${fmtDateTimeFirm(new Date().toISOString())}\n\n` +   // DD-MMM-YYYY HH:mm (owner date rule)
       codes.map((c, i) => `${(i + 1).toString().padStart(2, "0")}. ${c}`).join("\n") + "\n"],
      { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "azex-backup-codes.txt";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const lastAt = status?.last_2fa_at
    ? fmtDateTimeFirm(status.last_2fa_at)
    : "—";

  return (
    <>
      <Card data-testid="twofa-section">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            <CardTitle className="text-base">{tr("title")}</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 text-sm md:grid-cols-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{tr("status")}</div>
            <div className="mt-1 inline-flex items-center gap-1 rounded-md bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
              <ShieldCheck className="h-3 w-3" /> {tr("enabledEmail")}
            </div>
            {status?.in_grace_window && (
              <div className="mt-1 text-[11px] text-amber-700">{tr("grace")}</div>
            )}
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{tr("lastVerified")}</div>
            <div className="mt-1 font-medium">{lastAt}</div>
            <div className="text-[11px] text-muted-foreground">
              {tr("stepupEvery").replace("{n}", String(status?.stepup_validity_minutes ?? 10))}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{tr("backupCodes")}</div>
            <div className="mt-1 font-medium" data-testid="twofa-remaining-codes">
              {tr("remaining").replace("{n}", String(status?.backup_codes_remaining ?? "…"))}
            </div>
            <Button size="sm" variant="outline" className="mt-2"
                    onClick={() => setConfirmOpen(true)} disabled={busy}
                    data-testid="twofa-regen-btn">
              <RefreshCw className="me-1 h-3 w-3" />
              {status?.backup_codes_remaining ? tr("regenerate") : tr("generate")}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{tr("confirmTitle")}</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground">
            {tr("confirmBody")}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>{tr("cancel")}</Button>
            <Button onClick={regenerate} disabled={busy}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="twofa-regen-confirm">
              <KeyRound className="me-1 h-3 w-3" />
              {busy ? tr("working") : tr("regenerateShort")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCodes} onOpenChange={setShowCodes}>
        <DialogContent data-testid="twofa-new-codes-dialog">
          <DialogHeader>
            <DialogTitle>{tr("newTitle")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
              {tr("saveNow")}
            </div>
            <div className="grid grid-cols-2 gap-2 rounded-lg border bg-slate-50 p-3 font-mono text-sm">
              {codes.map((c, i) => (
                <div key={c} className="rounded bg-white px-3 py-2 tracking-widest">
                  <span className="me-2 text-[10px] text-muted-foreground">{(i + 1).toString().padStart(2, "0")}</span>{c}
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={copyAll} className="flex-1"><Copy className="me-1 h-4 w-4" /> {tr("copyAll")}</Button>
              <Button variant="outline" onClick={download} className="flex-1"><Download className="me-1 h-4 w-4" /> {tr("download")}</Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowCodes(false)} className="bg-slate-900 hover:bg-slate-800">{tr("saved")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
